import { useRef, useState } from 'react';
import { Account } from 'vopay';
import { api } from '@/services/api.config';

/**
 * One account with the details the listing leaves out unless asked for — API key, rolling-reserve
 * policy and limit usage to date. Same endpoint as the list, scoped to a single account; the
 * `IncludeAccountDetails` flag is what makes it cost six extra queries on the backend, so it is
 * only ever sent for one account at a time.
 */
export default function useFetchPartnerAccountDetails() {
  const [fetching, setFetching] = useState(false);
  const [account, setAccount] = useState<Account>();

  /**
   * Only the newest request may write to state. The drawer refetches as the user moves from one
   * account to the next, and nothing cancels the request already in flight — so without this an
   * earlier response landing last would put one account's reserve policy and limits under another
   * account's name.
   */
  const latestRequestId = useRef(0);

  async function fetchPartnerAccountDetails(accountId: string) {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    const isLatest = () => latestRequestId.current === requestId;

    try {
      setFetching(true);

      const res = await api.fetchPartnerAccounts({
        VopayAccountID: accountId,
        Limit: 1,
        ShowToken: true,
        IncludeAccountDetails: true,
      });

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      const matched = res.Accounts?.find((candidate) => candidate.AccountID === accountId);

      if (!matched) {
        throw new Error(`Account ${accountId} was not in the partner account response`);
      }

      if (isLatest()) {
        setAccount(matched);
      }

      return matched;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      if (isLatest()) {
        setFetching(false);
      }
    }
  }

  function reset() {
    // A reset supersedes whatever is in flight, so that response can no longer land on the cleared
    // state — which is what makes closing the drawer and opening another account safe.
    latestRequestId.current += 1;
    setAccount(undefined);
  }

  return { account, fetching, fetchPartnerAccountDetails, reset };
}
